import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Trash2, FolderOpen, FileText, Sparkles, Code2, Library, ClipboardList, LogOut, Shield } from "lucide-react";
import { api } from "@/lib/api";
import DocumentsPanel from "@/components/DocumentsPanel";
import AIPanel from "@/components/AIPanel";
import NewProjectDialog from "@/components/NewProjectDialog";
import ScriptsPanel from "@/components/ScriptsPanel";
import CxAlloyPanel from "@/components/CxAlloyPanel";
import AdminUsersDialog from "@/components/AdminUsersDialog";
import { confirmDialog } from "@/lib/dialogs";
import { useAuth } from "@/lib/auth";

const TABS = [
  { id: "documents", label: "Documents", icon: FileText },
  { id: "review", label: "AI Review", icon: Sparkles },
  { id: "script", label: "Script Lab", icon: Code2 },
  { id: "cxalloy", label: "CxAlloy", icon: ClipboardList },
  { id: "library", label: "Library", icon: Library },
];

export default function Workspace() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("documents");
  const [showNew, setShowNew] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [docsVersion, setDocsVersion] = useState(0);
  const [libraryVersion, setLibraryVersion] = useState(0);
  const [adminOpen, setAdminOpen] = useState(false);
  const { user, logout } = useAuth();
  const authRequired = true;

  const loadProjects = useCallback(async (q) => {
    try {
      const list = await api.listProjects(q);
      setProjects(list);
      return list;
    } catch (e) {
      toast.error("Failed to load projects");
      return [];
    }
  }, []);

  useEffect(() => {
    loadProjects("").then((list) => {
      if (projectId) {
        const found = list.find((p) => p.id === projectId);
        if (found) setActive(found);
        else navigate("/", { replace: true });
      } else if (list.length && !active) {
        setActive(list[0]);
        navigate(`/p/${list[0].id}`, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadProjects(query), 250);
    return () => clearTimeout(t);
  }, [query, loadProjects]);

  const onSelectProject = (p) => {
    setActive(p);
    setSelectedDocs([]);
    setTab("documents");
    navigate(`/p/${p.id}`);
  };

  const onCreated = async (proj) => {
    setShowNew(false);
    await loadProjects("");
    setActive(proj);
    setSelectedDocs([]);
    navigate(`/p/${proj.id}`);
    toast.success(`Project "${proj.name}" created`);
  };

  const onDeleteProject = async (e, p) => {
    e.stopPropagation();
    const ok = await confirmDialog({
      title: `Delete project "${p.name}"?`,
      message: "All documents, AI threads, and saved scripts in this project will be permanently removed.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteProject(p.id);
      toast.success("Project deleted");
      const list = await loadProjects("");
      if (active?.id === p.id) {
        const next = list[0] || null;
        setActive(next);
        navigate(next ? `/p/${next.id}` : "/");
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="w-full h-screen overflow-hidden flex flex-col md:flex-row bg-[#000000]" data-testid="workspace-root">
      {/* SIDEBAR */}
      <aside className="w-full md:w-72 border-r border-[#2D7544] bg-black h-full flex flex-col shrink-0" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-[#2D7544]">
          <div className="dv-overline mb-1">[ system / v1 ]</div>
          <h1 className="dv-h1" data-testid="brand-title">
            CxAssistant<span className="text-[#00FF41] dv-glow">_</span>
          </h1>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#3D8657] mt-2">
            &gt; upload · review · create
          </div>
        </div>

        <div className="px-4 py-3 border-b border-[#2D7544] flex items-center gap-2">
          <Search size={14} className="text-[#66B07A] shrink-0" />
          <input
            data-testid="project-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects…"
            className="w-full bg-transparent text-sm focus:outline-none placeholder:text-[#3D8657]"
          />
        </div>

        <button
          data-testid="new-project-btn"
          onClick={() => setShowNew(true)}
          className="mx-4 mt-4 mb-2 dv-btn-primary flex items-center justify-center gap-2"
        >
          <Plus size={14} /> New Project
        </button>

        <div className="flex-1 overflow-y-auto px-4 pb-4 mt-2" data-testid="project-list">
          {projects.length === 0 ? (
            <div className="text-xs text-[#66B07A] py-6 text-center border border-dashed border-[#2D7544] mt-2">
              No projects yet. Create one to begin.
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {projects.map((p) => (
                <li
                  key={p.id}
                  data-testid={`project-item-${p.id}`}
                  onClick={() => onSelectProject(p)}
                  className={`group border p-3 cursor-pointer transition-all ${
                    active?.id === p.id
                      ? "bg-[#0A2A14] border-[#00FF41] text-[#00FF41] shadow-[0_0_12px_rgba(0,255,65,0.25)_inset]"
                      : "bg-[#050805] border-[#2D7544] text-[#66B07A] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#00FF41]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FolderOpen size={14} />
                        <div className={`font-bold text-sm truncate ${active?.id === p.id ? "text-[#00FF41]" : "text-[#00FF41]"}`}>{active?.id === p.id ? "> " : ""}{p.name}</div>
                      </div>
                      <div className={`text-[11px] mt-1 truncate ${active?.id === p.id ? "text-[#66B07A]" : "text-[#66B07A]"}`}>
                        {p.description || "No description"}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(p.tags || []).slice(0, 3).map((t) => (
                          <span
                            key={t}
                            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#2D7544] text-[#66B07A]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      data-testid={`delete-project-${p.id}`}
                      onClick={(e) => onDeleteProject(e, p)}
                      className="p-1 border border-transparent transition-all hover:border-[#FF3344] hover:text-[#FF3344]"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-[#3D8657]">
                    [{(p.document_count || 0).toString().padStart(2, "0")}] docs
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#2D7544] font-mono text-[10px] uppercase tracking-widest text-[#3D8657] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate" data-testid="current-user-email">&gt; {user?.email || "anon"}</span>
            {user?.role === "admin" && (
              <button
                data-testid="open-admin-btn"
                onClick={() => setAdminOpen(true)}
                className="flex items-center gap-1 text-[#00FF41] hover:text-[#00FFC9] transition-colors"
                title="Manage users"
              >
                <Shield size={12} /> admin
              </button>
            )}
          </div>
          {authRequired && (
            <button
              data-testid="logout-btn"
              onClick={async () => {
                const ok = await confirmDialog({
                  title: "Sign out?",
                  message: "You'll need to sign back in.",
                  confirmLabel: "Sign out",
                });
                if (ok) logout();
              }}
              className="flex items-center gap-1 text-[#66B07A] hover:text-[#00FF41] transition-colors"
              title="Sign out"
            >
              <LogOut size={12} /> exit
            </button>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 flex flex-col h-full bg-black overflow-hidden" data-testid="main-panel">
        {!active ? (
          <EmptyState onCreate={() => setShowNew(true)} />
        ) : (
          <>
            {/* Header */}
            <header className="border-b border-[#2D7544] px-6 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="dv-overline mb-1">Active Project</div>
                <h2 className="font-heading text-2xl font-extrabold tracking-tighter leading-none truncate">
                  {active.name}
                </h2>
                {active.description && (
                  <p className="text-sm text-[#66B07A] mt-1 truncate">{active.description}</p>
                )}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#66B07A] hidden md:block">
                ID · {active.id.slice(0, 8)}
              </div>
            </header>

            {/* Tabs */}
            <div className="border-b border-[#2D7544] flex" data-testid="tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                const isActive = tab === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`px-5 py-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider border-r border-[#2D7544] transition-colors ${
                      isActive ? "bg-[#00FF41] text-[#000000]" : "bg-[#050805] hover:bg-[#0D1F12]"
                    }`}
                  >
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
              {selectedDocs.length > 0 && (
                <div className="ml-auto px-4 flex items-center font-mono text-[11px] tracking-widest text-[#00FFC9]">
                  {selectedDocs.length} DOC{selectedDocs.length > 1 ? "S" : ""} SELECTED
                </div>
              )}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-hidden">
              {tab === "documents" && (
                <DocumentsPanel
                  key={`docs-${active.id}-${docsVersion}`}
                  project={active}
                  selected={selectedDocs}
                  onSelectedChange={setSelectedDocs}
                  onMutated={() => {
                    setDocsVersion((v) => v + 1);
                    loadProjects(query);
                  }}
                />
              )}
              {tab === "review" && (
                <AIPanel mode="review" project={active} selectedDocs={selectedDocs} />
              )}
              {tab === "script" && (
                <AIPanel
                  mode="script"
                  project={active}
                  selectedDocs={selectedDocs}
                  onScriptSaved={() => setLibraryVersion((v) => v + 1)}
                />
              )}
              {tab === "cxalloy" && (
                <CxAlloyPanel
                  project={active}
                  selectedDocs={selectedDocs}
                  onScriptSaved={() => setLibraryVersion((v) => v + 1)}
                />
              )}
              {tab === "library" && (
                <ScriptsPanel
                  project={active}
                  selectedDocs={selectedDocs}
                  refreshKey={libraryVersion}
                />
              )}
            </div>
          </>
        )}
      </main>

      <NewProjectDialog open={showNew} onClose={() => setShowNew(false)} onCreated={onCreated} />
      <AdminUsersDialog open={adminOpen} onClose={() => setAdminOpen(false)} currentUserId={user?.id} />
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center" data-testid="empty-state">
      <div className="dv-overline mb-3">[ CxAssistant ]</div>
      <h2 className="dv-h1 max-w-xl" style={{ fontSize: 40, lineHeight: 1.05 }}>
        upload. review. create<span className="dv-glow">_</span>
      </h2>
      <p className="text-[12px] text-[#66B07A] mt-5 max-w-md leading-relaxed uppercase tracking-widest">
        &gt; drop in specs, sequences of operation, submittals
        <br />
        &gt; let claude sonnet 4.5 review them
        <br />
        &gt; generate cxalloy-ready fpts &amp; checklists
      </p>
      <button data-testid="empty-create-project" onClick={onCreate} className="dv-btn-primary mt-8 flex items-center gap-2">
        <Plus size={14} /> Init project
      </button>
    </div>
  );
}
